import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import BrokerLandingPageFooterView from '@/website/components/footers/brokerLandingPageFooter';
import { useLocaleContext } from '@/website/contexts/AppContext';
import { promotionInfoStore } from '@/website/store/promotion/promotionInfoStore';
import {
  AmericanCountryList,
  EuropeanUnionList,
  PromotionEntityType,
  apiQueryUserIpAndCountryInfo,
  openUrlWithNewWindow,
  useLocalTranslation,
} from '@jove-frontend/uikit';
import { getLegalLinkHrefWithCountry, getLegalLinks, socialLinks } from '../util';

enum CountryEnum {
  UK = 'UK',
  US = 'US',
  EU = 'EU',
}

enum FooterTypeEnum {
  normal = 'normal',
  broker = 'broker',
}

const Footer = () => {
  const { $t } = useLocalTranslation();
  const [companyRegistrationText, setCompanyRegistrationText] = useState('');
  const [addressText, setAddressText] = useState('');
  const [copyRightText, setCopyRightText] = useState('');
  const {
    state: { promotionInfo },
  } = promotionInfoStore();
  const [legalLinkList, setLegalLinkList] = useState(getLegalLinks());
  const [socialLinkList, setSocialLinkList] = useState(socialLinks);
  const [footerType, setFooterType] = useState<FooterTypeEnum>(FooterTypeEnum.normal);
  const { country } = useLocaleContext();

  /**
   * request
   */
  const requestUserCountryInfo = async () => {
    const [res] = await apiQueryUserIpAndCountryInfo();

    return res?.country;
  };

  /**
   * effects
   */
  useEffect(() => {
    if ([PromotionEntityType.BROKER_PROMOTION, PromotionEntityType.PARTNER_PROMOTION].includes(promotionInfo?.promotion_type)) {
      setFooterType(FooterTypeEnum.broker);
    } else {
      if (country) {
        updateFooterJoveCompanyInfo(country);
      } else {
        evaluateCompanyInfoText();
      }
    }
  }, [promotionInfo, country]);

  useEffect(() => {
    const year = new Date().getFullYear();
    setCopyRightText($t('page:footer.copyright', { year }));
    setLegalLinkList(getLegalLinks());
  }, [$t]);

  /**
   * methods
   */
  const evaluateCompanyInfoText = async () => {
    const country = await requestUserCountryInfo();

    updateFooterJoveCompanyInfo(country);
  };

  const updateFooterJoveCompanyInfo = (country) => {
    let registText = '';
    let addressText = '';
    if (EuropeanUnionList.includes(country)) {
      registText = $t('page:footer.jove_eu_registration');
      addressText = $t('page:footer.jove_eu_address');
    } else if (AmericanCountryList.includes(country)) {
      registText = $t('page:footer.jove_us_registration');
      addressText = $t('page:footer.jove_us_address');
    } else {
      registText = $t('page:footer.jove_uk_registration');
      addressText = $t('page:footer.jove_uk_address');
    }

    setCompanyRegistrationText(registText);
    setAddressText(addressText);
  };

  const clickLegalLinkAction = (e: React.MouseEvent, item) => {
    e.preventDefault();
    openUrlWithNewWindow(getLegalLinkHrefWithCountry(item.href, country));
  };

  return footerType === FooterTypeEnum.broker ? (
    <BrokerLandingPageFooterView />
  ) : (
    <footer className="footer border-t-2 bg-black/75">
      <div className="mx-auto max-w-7xl items-center border-gray-100 px-4 py-6 sm:px-6 md:flex md:justify-between lg:px-8">
        <div className="flex justify-center space-x-6 md:order-2">
          {socialLinkList.map((item) => (
            <a
              key={item.name}
              href={item.href}
              className="transition-opacity duration-200 hover:opacity-80"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="sr-only">{item.name}</span>
              <item.icon className="h-6 w-6" aria-hidden="true" />
            </a>
          ))}
        </div>
        <div className="mt-8 text-center md:order-1 md:mt-0 ">
          <div>
            {legalLinkList.map((item, index) => (
              <Link
                href={item.href}
                passHref
                key={item.name + index}
                className="ml-4 border-r border-r-white pr-4 text-sm leading-3 text-white first:ml-0 last:border-r-0 hover:underline"
                onClick={(e) => clickLegalLinkAction(e, item)}
              >
                {item.name}
              </Link>
            ))}
          </div>

          <p className="mt-1 text-center text-sm text-gray-400 sm:text-left">{copyRightText}</p>
        </div>
      </div>
      <p className="mt-1 pb-4 text-center text-sm text-gray-400">{companyRegistrationText}</p>
      <p className="mt-1 pb-4 text-center text-sm text-gray-400">{addressText}</p>
    </footer>
  );
};

export default Footer;
